/**
 * Calendar Filters Component
 *
 * Compact filter toolbar for status, resource, and external calendar toggle.
 */

import React, { useMemo } from 'react';
import { Flex, Typography, Select, Button, Switch, theme, Skeleton } from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { type AppointmentStatus } from '@/api/mcp-appointments';
import { FilterBar } from '@/components/ui/FilterBar';
import type { CalendarResourceFilter } from './calendarUtils';
import { getStatusBackgroundColor } from './calendarUtils';

const { Text } = Typography;

interface CalendarFiltersProps {
  selectedStatus: AppointmentStatus | 'ALL';
  selectedResourceId: CalendarResourceFilter;
  uniqueStatuses: AppointmentStatus[];
  resourceOptions: Array<{ id: string; name: string; type: string }>;
  isResourcesLoading?: boolean;
  onStatusChange: (status: AppointmentStatus | 'ALL') => void;
  onResourceIdChange: (resourceId: CalendarResourceFilter) => void;
  onClearFilters: () => void;
  showExternalCalendars: boolean;
  onShowExternalCalendarsChange: (value: boolean) => void;
}

const selectPopupStyles = { popup: { root: { zIndex: 1050 } } };

export const CalendarFilters: React.FC<CalendarFiltersProps> = ({
  selectedStatus,
  selectedResourceId,
  uniqueStatuses,
  resourceOptions,
  isResourcesLoading = false,
  onStatusChange,
  onResourceIdChange,
  onClearFilters,
  showExternalCalendars,
  onShowExternalCalendarsChange
}) => {
  const { t } = useTranslation('manage');
  const { token } = theme.useToken();

  const hasActiveFilters = selectedStatus !== 'ALL';
  const hasResources = resourceOptions.length > 0;

  const resourceSelectValue = useMemo(() => {
    if (!hasResources) return undefined;
    if (selectedResourceId === 'ALL') return 'ALL';
    if (resourceOptions.some((r) => r.id === selectedResourceId)) {
      return selectedResourceId;
    }
    return 'ALL';
  }, [hasResources, selectedResourceId, resourceOptions]);

  if (isResourcesLoading || !hasResources) {
    return (
      <FilterBar>
        {isResourcesLoading ? (
          <Skeleton active title={false} paragraph={{ rows: 1, width: '100%' }} />
        ) : (
          <Text type="secondary">{t('appointmentComponents.filters.noResources')}</Text>
        )}
      </FilterBar>
    );
  }

  return (
    <FilterBar
      actions={
        <>
          {hasActiveFilters && (
            <Button type="text" icon={<ClearOutlined />} onClick={onClearFilters}>
              {t('appointmentComponents.filters.clearFilters')}
            </Button>
          )}
          <Flex align="center" gap={token.marginXS} component="label" style={{ cursor: 'pointer' }}>
            <Switch size="small" checked={showExternalCalendars} onChange={onShowExternalCalendarsChange} />
            <Text style={{ fontSize: token.fontSizeSM }}>{t('appointmentComponents.filters.showExternalCalendars')}</Text>
          </Flex>
        </>
      }>
      <Select
        aria-label={t('appointmentComponents.filters.status')}
        value={selectedStatus}
        onChange={onStatusChange}
        style={{ flex: '1 1 160px', minWidth: 140, maxWidth: 240 }}
        styles={selectPopupStyles}
        getPopupContainer={() => document.body}>
        <Select.Option value="ALL">{t('appointmentComponents.filters.allStatuses')}</Select.Option>
        {uniqueStatuses.map((status) => (
          <Select.Option key={status} value={status}>
            <Flex align="center" gap={token.marginXS}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: getStatusBackgroundColor(status),
                  flexShrink: 0
                }}
              />
              <span>{t(`appointmentComponents.filters.statusLabels.${status}`)}</span>
            </Flex>
          </Select.Option>
        ))}
      </Select>

      <Select
        aria-label={t('appointmentComponents.filters.resource')}
        value={resourceSelectValue}
        onChange={onResourceIdChange}
        style={{ flex: '2 1 220px', minWidth: 160, maxWidth: 360 }}
        showSearch
        styles={selectPopupStyles}
        filterOption={(input, option) => {
          const label = typeof option?.label === 'string' ? option.label : String(option?.label ?? '');
          return label.toLowerCase().includes(input.toLowerCase());
        }}
        getPopupContainer={() => document.body}>
        <Select.Option value="ALL" label={t('appointmentComponents.filters.allResources')}>
          {t('appointmentComponents.filters.allResources')}
        </Select.Option>
        {resourceOptions.map((resource) => (
          <Select.Option key={resource.id} value={resource.id} label={resource.name}>
            {resource.name} ({t(`resources.filters.${resource.type.toLowerCase()}`, { defaultValue: resource.type })})
          </Select.Option>
        ))}
      </Select>
    </FilterBar>
  );
};
